import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Search, ImageIcon, Check, Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Variant, ProductImage } from './VariantForm';

interface ProductSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  refreshKey?: number;
  onAddProduct?: () => void;
  onConfirm?: (items: Array<{ id: string; nombre: string; thumbnail: string | null }>) => void;
}

export const ProductSelectorModal: React.FC<ProductSelectorModalProps> = ({ isOpen, onClose, refreshKey = 0, onAddProduct, onConfirm }) => {
  const [variants, setVariants] = useState<Variant[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [signedUrls, setSignedUrls] = useState<Record<string, { full: string; thumbnail: string }>>({});

  const cardRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingImageKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen) return;
    void fetchVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, refreshKey]);

  const fetchVariants = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company, company_id')
        .eq('id', user.id)
        .single();

      let compId = (profile as any)?.id_company as string | null;
      if (!compId && (profile as any)?.company_id) {
        const { data: comp } = await supabase
          .from('company_profiles')
          .select('id_company')
          .eq('id', (profile as any).company_id)
          .single();
        compId = (comp as any)?.id_company ?? null;
      }
      if (!compId) throw new Error('No company associated with user');
      setCompanyId(compId);

      const { data: variantsData, error: variantsError } = await supabase
        .from('variantes')
        .select('id, nombre, created_at')
        .eq('activo', true)
        .eq('company_id', compId)
        .order('created_at', { ascending: false });

      if (variantsError) throw variantsError;

      let processed: Variant[] = [];
      if (variantsData && variantsData.length > 0) {
        const variantIds = variantsData.map(v => v.id as string);
        const { data: imagesData, error: imagesError } = await supabase
          .from('productos_imagenes')
          .select('id, url, principal, orden, variante_id')
          .eq('company_id', compId)
          .in('variante_id', variantIds);

        if (imagesError) throw imagesError;

        const imagesByVariantId: Record<string, ProductImage[]> = {};
        (imagesData || []).forEach((image: any) => {
          const variantId = image.variante_id as string;
          const entry: ProductImage = {
            id: image.id,
            url: image.url,
            principal: image.principal,
            orden: image.orden,
            producto_variante_id: 0,
            variante_id: variantId,
          };
          if (!imagesByVariantId[variantId]) imagesByVariantId[variantId] = [];
          imagesByVariantId[variantId].push(entry);
        });

        processed = variantsData.map((v: any) => ({ id: v.id as string, nombre: v.nombre, images: imagesByVariantId[v.id as string] || [] }));
      }

      setVariants(processed);
      setSignedUrls({});
      setSelected([]);
    } catch (err) {
      console.error('Error fetching variants (modal):', err);
      setError(err instanceof Error ? err.message : 'Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const principalImageUrl = (variant: Variant): string | null => {
    const img = (variant.images || []).find(i => i.principal) || (variant.images || [])[0];
    if (!img?.url) return null;
    const urlKey = img.url;
    if (urlKey.startsWith('http')) return urlKey;
    const signed = signedUrls[urlKey];
    return signed?.thumbnail || null;
  };

  const ensurePrincipalImageSigned = async (variantId: string) => {
    const variant = variants.find(v => v.id === variantId);
    if (!variant) return;
    const img = (variant.images || []).find(i => i.principal) || (variant.images || [])[0];
    if (!img?.url) return;
    const key = img.url as string;
    if (key.startsWith('http')) {
      if (!signedUrls[key]) {
        setSignedUrls(prev => ({ ...prev, [key]: { full: key, thumbnail: key } }));
      }
      return;
    }
    if (!companyId) return;
    if (signedUrls[key] || loadingImageKeysRef.current.has(key)) return;

    loadingImageKeysRef.current.add(key);
    try {
      const pathVariantId = (img as any)?.variante_id ?? variant.id;
      const optFilePath = `${companyId}/${pathVariantId}/opt/${key}`;
      const fullFilePath = `${companyId}/${pathVariantId}/full/${key}`;

      let thumbSigned: string | null = null;
      let fullSigned: string | null = null;

      const { data: thumb } = await supabase.storage.from('productos').createSignedUrl(optFilePath, 3600);
      if (thumb?.signedUrl) {
        thumbSigned = thumb.signedUrl;
        const { data: full } = await supabase.storage.from('productos').createSignedUrl(fullFilePath, 3600);
        fullSigned = full?.signedUrl || thumbSigned;
      } else {
        const { data: full } = await supabase.storage.from('productos').createSignedUrl(fullFilePath, 3600);
        if (full?.signedUrl) {
          thumbSigned = full.signedUrl;
          fullSigned = full.signedUrl;
        }
      }

      setSignedUrls(prev => ({ ...prev, [key]: { full: fullSigned || key, thumbnail: thumbSigned || fullSigned || key } }));
    } catch (e) {
      setSignedUrls(prev => ({ ...prev, [key]: { full: 'https://via.placeholder.com/300x300?text=Error', thumbnail: 'https://via.placeholder.com/300x300?text=Error' } }));
    } finally {
      loadingImageKeysRef.current.delete(key);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target as HTMLElement;
          const idAttr = el.getAttribute('data-variant-id');
          const vid = idAttr || '';
          if (vid) {
            void ensurePrincipalImageSigned(vid as string);
          }
          observerRef.current?.unobserve(entry.target);
        }
      });
    }, { root: null, rootMargin: '200px 0px', threshold: 0.01 });

    const vs = filteredVariants;
    vs.forEach((v) => {
      const el = cardRefs.current[String(v.id)];
      if (el) observerRef.current!.observe(el);
    });

    return () => observerRef.current?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, variants, companyId, query]);

  const filteredVariants = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return variants;
    return variants.filter(v => (v.nombre || '').toLowerCase().includes(q));
  }, [variants, query]);

  const [animated, setAnimated] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(0);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimated(true)));
      document.body.style.overflow = 'hidden';
    } else {
      setAnimated(false);
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setAnimated(false);
    setTimeout(() => onClose(), 300);
  }, [onClose]);

  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };
  const handleTouchEnd = () => {
    if (dragY > 100) handleClose();
    else setDragY(0);
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleConfirm = async () => {
    await Promise.all(selected.map(async (id) => ensurePrincipalImageSigned(id)));
    const payload = selected.map((id) => {
      const variant = variants.find(v => v.id === id)!;
      const thumb = principalImageUrl(variant);
      return { id, nombre: variant.nombre, thumbnail: thumb };
    });
    if (onConfirm) onConfirm(payload);
    onClose();
  };

  if (!isOpen) return null;

  const searchBar = (
    <div className="px-4 sm:px-5 py-4">
      <label htmlFor="search-productos-modal" className="sr-only">Buscar productos</label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-black/40" />
        <input
          id="search-productos-modal"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar productos..."
          className="w-full pl-10 pr-3 h-11 sm:h-12 rounded-2xl bg-neutral-50 sm:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary text-[15px] placeholder:text-black/30"
        />
      </div>
    </div>
  );

  const errorMsg = error ? (
    <div className="px-4 sm:px-5 mb-2 text-[15px] text-error">{error}</div>
  ) : null;

  const productGrid = loading ? (
    <div className="px-4 sm:px-5 pb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="bg-white border border-neutral-200 rounded-md overflow-hidden">
          <div className="w-full aspect-square bg-neutral-100 animate-pulse" />
          <div className="p-3">
            <div className="h-4 bg-neutral-100 rounded w-2/3 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className="px-4 sm:px-5 pb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {/* Add product card */}
      <button
        type="button"
        onClick={() => { if (onAddProduct) onAddProduct(); }}
        className="relative text-left bg-white border-0 rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary group"
      >
        <div className="relative pt-0 px-0 sm:pt-3 sm:px-3">
          <div className="w-full aspect-square bg-white flex items-center justify-center rounded-b-2xl sm:rounded-lg">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-brand-primary text-white flex items-center justify-center transition-colors group-hover:bg-brand-primary-hover">
              <Plus className="w-8 h-8 sm:w-9 sm:h-9" />
            </div>
          </div>
        </div>
        <div className="p-3">
          <p className="text-[15px] text-black/90 truncate font-medium py-1">Agregar producto</p>
        </div>
      </button>

      {/* Variants */}
      {filteredVariants.length > 0 && filteredVariants.map(variant => {
        const imgUrl = principalImageUrl(variant);
        const isSelected = selected.includes(variant.id);
        return (
          <button
            key={variant.id}
            type="button"
            onClick={() => toggleSelect(variant.id)}
            className={`relative text-left bg-white border-0 ${isSelected ? 'ring-2 ring-brand-primary' : ''} rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary group`}
            ref={(el) => { cardRefs.current[variant.id] = el; }}
            data-variant-id={variant.id}
          >
            <div className="relative pt-0 px-0 sm:pt-3 sm:px-3">
              {imgUrl ? (
                <img src={imgUrl} alt={variant.nombre} loading="lazy" className="w-full aspect-square object-cover rounded-b-2xl sm:rounded-lg" />
              ) : (
                <div className="w-full aspect-square bg-neutral-100 flex items-center justify-center rounded-b-2xl sm:rounded-lg">
                  <ImageIcon className="w-8 h-8 text-black/30" />
                </div>
              )}
              <div className={`absolute top-2 right-2 sm:top-4 sm:right-4 w-7 h-7 rounded-full ${isSelected ? 'bg-brand-primary text-white' : 'bg-white/80 text-black/20 group-hover:text-black/30 backdrop-blur-sm'} flex items-center justify-center shadow-none transition-colors`}>
                <Check className="w-4 h-4" />
              </div>
            </div>
            <div className="p-3">
              <p className="text-[15px] text-black/90 truncate font-medium py-1">{variant.nombre}</p>
            </div>
          </button>
        );
      })}
    </div>
  );

  const confirmButton = selected.length > 0 ? (
    <button
      type="button"
      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 h-10 bg-brand-primary text-white rounded-full shadow-[0_6px_18px_rgba(0,0,0,0.35)] sm:shadow-sm hover:bg-brand-primary-hover text-[17px] sm:text-[15px] whitespace-nowrap"
      onClick={handleConfirm}
    >
      {`Seleccionar ${selected.length} ${selected.length === 1 ? 'producto' : 'productos'}`}
    </button>
  ) : null;

  return (
    <>
      {/* ── Desktop: centered modal ── */}
      <div className="hidden sm:flex fixed inset-0 z-50 items-center justify-center p-4">
        <div
          className={`absolute inset-0 transition-opacity duration-300 ${animated ? 'bg-black/40 opacity-100' : 'bg-black/40 opacity-0'}`}
          onClick={handleClose}
        />
        <div className="relative bg-white rounded-[13px] shadow-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 shrink-0">
            <h2 className="text-[17px] font-semibold text-black/90">Seleccioná productos</h2>
            <button onClick={handleClose} className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-black/40 hover:bg-black/[0.1] transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mx-5 h-px bg-black/[0.08]" />
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            {searchBar}
            {errorMsg}
            {productGrid}
          </div>
          {/* Footer */}
          {confirmButton && (
            <div className="px-5 py-3 flex justify-end border-t border-black/[0.08] shrink-0">
              {confirmButton}
            </div>
          )}
        </div>
      </div>

      {/* ── Mobile: bottom sheet via portal ── */}
      {ReactDOM.createPortal(
        <>
          <div
            className={`sm:hidden fixed inset-0 z-[200] transition-opacity duration-300 ${animated ? 'bg-black/40 opacity-100' : 'bg-black/40 opacity-0'}`}
            onClick={handleClose}
          />
          <div
            className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] shadow-lg flex flex-col"
            style={{
              // Restamos `env(safe-area-inset-top)` para que el tope
              // del sheet quede por debajo de la status bar
              // translúcida (PWA standalone con viewport-fit=cover).
              height: 'calc(100dvh - 1rem - env(safe-area-inset-top, 0px))',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
              transform: dragY > 0
                ? `translateY(${dragY}px)`
                : animated ? 'translateY(0)' : 'translateY(100%)',
              transition: dragY === 0 ? 'transform 0.3s ease-out' : 'none',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div
              className="py-2 cursor-grab active:cursor-grabbing shrink-0"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
            </div>
            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3 shrink-0">
              <button type="button" onClick={handleClose} className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left">Cerrar</button>
              <h3 className="text-[15px] font-semibold text-black/90">Productos</h3>
              <span className="w-20" />
            </div>
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              {searchBar}
              {errorMsg}
              {productGrid}
            </div>
            {/* Floating confirm */}
            {confirmButton && (
              <div className="px-4 py-3 shrink-0">
                {confirmButton}
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  );
}

export default ProductSelectorModal;
